import type { AdminSearchQueryParams, AdminSearchResultsResponse } from '../types';
import { useInfiniteQuery } from '@tanstack/react-query';
import FetchPromise from '~/agent/fetch-promise';
import HttpUtils from '~/agent/http-utils';
import { APP_CONSTANTS } from '~/common/explicit-exports';
import { ADMIN_SEARCH_BASE_URL } from '../constants';

const getAdminSearchResults =
  (queryParams: AdminSearchQueryParams) =>
  ({
    pageParam = { offset: queryParams.offset ?? 0, folderOffset: queryParams.folderOffset ?? 0 },
  }): Promise<AdminSearchResultsResponse> => {
    // Always pass both offsets, falling back to queryParams or 0
    const offset = pageParam.offset ?? queryParams.offset ?? 0;
    const folderOffset = pageParam.folderOffset ?? queryParams.folderOffset ?? 0;

    // Pass all params, let HttpUtils handle array serialization
    const queryData = {
      ...queryParams,
      offset,
      folderOffset,
      query: queryParams.query || '',
    };

    return FetchPromise.get(HttpUtils.buildUriWithQueryString(ADMIN_SEARCH_BASE_URL, queryData));
  };

export const useAdminSearchResults = (queryParams: AdminSearchQueryParams, isEnabled: boolean = true) =>
  useInfiniteQuery<AdminSearchResultsResponse, JQuery.jqXHR>(
    ['adminSearchResults', queryParams],
    getAdminSearchResults(queryParams),
    {
      enabled: isEnabled,
      getNextPageParam: (lastPage) => {
        const { content, folders } = lastPage.pagination;
        // Check if we got a full page of results for each index.
        // If we did, we know there are more results to fetch
        // Otherwise, we know we've reached the end of the results
        const HITS_PER_INDEX = APP_CONSTANTS.ADMIN_SEARCH.SEARCH_RESULTS_LIMIT;
        const hasMoreContent = content.total === HITS_PER_INDEX;
        const hasMoreFolders = folders.total === HITS_PER_INDEX;

        if (hasMoreContent || hasMoreFolders) {
          return {
            // Always pass the current offset, even if we're not fetching more of that type
            // This prevents resetting to default offset of ZERO
            offset: hasMoreContent ? content.offset : content.offset,
            folderOffset: hasMoreFolders ? folders.offset : folders.offset,
          };
        }
        return undefined;
      },
    },
  );
